import React, { useEffect, useState } from 'react';
import { Meeting } from '../../services/meetingService';

/**
 * Who a meeting summary goes to.
 *
 * Lives on its own because the summary can be sent from more than one screen
 * and the rules are not obvious: the patient's copy is password locked and the
 * GP's is not, and a review that discharged the patient to their GP sends a
 * different covering letter. Duplicating that per screen is how the two drift
 * apart.
 */
export interface SummaryRecipients {
  patientEmail: string | null;
  gpEmail?: string;
}

interface Props {
  isOpen: boolean;
  meeting: Meeting;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (recipients: SummaryRecipients) => void;
}

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export const SummaryRecipientsDialog: React.FC<Props> = ({
  isOpen, meeting, busy = false, onCancel, onConfirm,
}) => {
  const gpOnFile = meeting.patient?.gp_email || '';
  const isDischargeToGP = meeting.treatment_decision === 'DISCHARGE_TO_GP';

  const [toPatient, setToPatient] = useState(true);
  const [patientEmail, setPatientEmail] = useState('');
  const [toGP, setToGP] = useState(false);
  const [gpEmail, setGpEmail] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setPatientEmail(meeting.patient?.email || '');
    setToPatient(!!meeting.patient?.email);
    setGpEmail(gpOnFile);
    // Normally off even when a GP address is on file: sending a patient's
    // clinical summary to a third party should be a decision, not a default.
    // A discharge is the exception - handing prescribing back is the point of
    // it, and this send is the only thing that tells the GP.
    setToGP(isDischargeToGP && !!gpOnFile);
    setError('');
  }, [isOpen, meeting.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const submit = () => {
    if (toPatient && !isValidEmail(patientEmail)) {
      setError('Please enter a valid patient email address');
      return;
    }
    if (toGP && !isValidEmail(gpEmail)) {
      setError('Please enter a valid GP email address');
      return;
    }
    setError('');
    onConfirm({
      patientEmail: toPatient ? patientEmail.trim() : null,
      gpEmail: toGP ? gpEmail.trim() : undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-[1500] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">Send summary</h3>
          <p className="text-xs text-gray-500 mt-1">
            {isDischargeToGP
              ? 'This review discharged the patient to their GP. The GP receives the shared-care discharge letter; the patient receives the usual summary.'
              : "The patient's copy is password protected. The GP receives a separate email with an unlocked copy."}
          </p>
        </div>

        <div className="px-6 py-4 space-y-3">
          <div className={`border rounded-xl p-4 transition-colors ${toPatient ? 'border-[#3A7380] bg-[#EAF4F3]' : 'border-gray-200 bg-white'}`}>
            <label className="flex items-center gap-3 cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={toPatient}
                onChange={(e) => { setToPatient(e.target.checked); setError(''); }}
                className="w-4 h-4 rounded accent-[#3A7380]"
              />
              <span className="font-semibold text-gray-800">Send to Patient</span>
            </label>
            {toPatient && (
              <div>
                <p className="text-xs text-gray-500 mb-1 font-medium">Patient Email</p>
                <input
                  type="email"
                  value={patientEmail}
                  onChange={(e) => { setPatientEmail(e.target.value); setError(''); }}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#3A7380]"
                />
              </div>
            )}
          </div>

          <div className={`border rounded-xl p-4 transition-colors ${toGP ? 'border-[#4A6FA5] bg-[#EEF2F9]' : 'border-gray-200 bg-white'}`}>
            <label className="flex items-center gap-3 cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={toGP}
                onChange={(e) => { setToGP(e.target.checked); setError(''); }}
                className="w-4 h-4 rounded accent-[#4A6FA5]"
              />
              <span className="font-semibold text-gray-800">
                {isDischargeToGP ? 'Send discharge letter to GP' : 'Send to GP'}
              </span>
            </label>
            {toGP && (
              <div>
                <p className="text-xs text-gray-500 mb-1 font-medium">GP Email</p>
                <input
                  type="email"
                  value={gpEmail}
                  onChange={(e) => { setGpEmail(e.target.value); setError(''); }}
                  placeholder="gp@practice.nhs.uk"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#4A6FA5]"
                />
                {!gpOnFile && (
                  <p className="text-xs text-gray-500 mt-1">
                    No GP address on file for this patient — it will be saved for next time.
                  </p>
                )}
                {isDischargeToGP && !gpEmail && (
                  <p className="text-xs text-amber-600 mt-1">
                    Without this, nothing tells the GP the patient has been discharged back to them.
                  </p>
                )}
              </div>
            )}
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>

        <div className="border-t border-gray-100 px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-5 py-2 rounded-xl border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || (!toPatient && !toGP)}
            className="px-5 py-2 rounded-xl bg-[#3A7380] text-white text-sm font-semibold hover:bg-[#2D5A60] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};
